// frontend/modules/template/components/TemplateList.tsx
import React, { useState } from 'react';
import { Table, Button, Space, Tag, Typography, Tooltip, Badge } from 'antd';
import { EditOutlined, DeleteOutlined, GlobalOutlined,
    EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useTemplates } from '../hooks/useTemplates';
import { Languages, NotificationPlatforms, TextTemplateDTO } from '@/shared/api/model';

const { Text } = Typography;

interface TemplateListProps {
    onEdit: (name: string, platform: NotificationPlatforms) => void;
    onDelete: (name: string, platform: NotificationPlatforms) => void;
}

const TemplateList: React.FC<TemplateListProps> = ({ onEdit, onDelete }) => {
    const {
        filteredTemplates,
        isLoading
    } = useTemplates();

    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

    // Group templates by name and platform
    const groupedTemplates = React.useMemo(() => {
        const templateMap = new Map<string, any>();

        filteredTemplates.forEach(template => {
            const key = `${template.name}_${template.platform}`;

            if (!templateMap.has(key)) {
                templateMap.set(key, {
                    key,
                    name: template.name,
                    platform: template.platform,
                    contentType: template.contentType,
                    languages: [],
                    templates: []
                });
            }

            const group = templateMap.get(key);
            group.languages.push(template.language);
            group.templates.push(template);
        });

        return Array.from(templateMap.values());
    }, [filteredTemplates]);

    const getPlatformColor = (platform: NotificationPlatforms) => {
        switch (platform) {
            case NotificationPlatforms.WHATS_APP:
                return 'green';
            case NotificationPlatforms.SMS:
                return 'blue';
            case NotificationPlatforms.EMAIL:
                return 'purple';
            default:
                return 'default';
        }
    };

    const getLanguageColor = (language: Languages) => {
        switch (language) {
            case Languages.EN:
                return 'blue';
            case Languages.SW:
                return 'orange';
            default:
                return 'default';
        }
    };

    const handleExpand = (expanded: boolean, record: any) => {
        if (expanded) {
            setExpandedRowKeys([...expandedRowKeys, record.key]);
        } else {
            setExpandedRowKeys(expandedRowKeys.filter(key => key !== record.key));
        }
    };

    const toggleAllRows = () => {
        if (expandedRowKeys.length === groupedTemplates.length) {
            // Collapse all
            setExpandedRowKeys([]);
        } else {
            // Expand all
            setExpandedRowKeys(groupedTemplates.map(template => template.key));
        }
    };

    const columns = [
        {
            title: 'Template Name',
            dataIndex: 'name',
            key: 'name',
            width: '25%',
            render: (text: string) => <strong>{text}</strong>
        },
        {
            title: 'Platform',
            dataIndex: 'platform',
            key: 'platform',
            width: '15%',
            render: (platform: NotificationPlatforms) => (
                <Tag color={getPlatformColor(platform)}>
                    {platform}
                </Tag>
            )
        },
        {
            title: 'Type',
            dataIndex: 'contentType',
            key: 'contentType',
            width: '10%',
        },
        {
            title: 'Languages',
            dataIndex: 'languages',
            key: 'languages',
            width: '20%',
            render: (languages: Languages[]) => (
                <Space size="small">
                    {languages.map(lang => (
                        <Tag color={getLanguageColor(lang)} key={lang}>
                            {lang}
                        </Tag>
                    ))}
                </Space>
            )
        },
        {
            title: (
                <div className="flex items-center">
                    <span className="mr-2">Actions</span>
                    <Tooltip title={expandedRowKeys.length === groupedTemplates.length ? "Collapse All" : "Expand All"}>
                        <Button
                            type="text"
                            size="small"
                            onClick={toggleAllRows}
                            icon={expandedRowKeys.length === groupedTemplates.length ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                        />
                    </Tooltip>
                </div>
            ),
            key: 'actions',
            width: '15%',
            render: (_: any, record: any) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record.name, record.platform)}
                            size="small"
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            onClick={() => onDelete(record.name, record.platform)}
                            size="small"
                        />
                    </Tooltip>
                </Space>
            )
        }
    ];

    const renderLanguageContent = (template: TextTemplateDTO) => {
        const maxLength = 300;
        const content = template.content || '';

        return (
            <div>
                <div className="mb-2">
                    <Badge
                        color={
                            // @ts-ignore
                            getLanguageColor(template.language)}
                        text={template.language === Languages.EN ? 'English' : 'Swahili'}
                    />
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 overflow-x-auto">
                    {content.length > maxLength ? (
                        <div>
                            <pre className="whitespace-pre-wrap">{content.substring(0, maxLength)}...</pre>
                            <Text type="secondary" className="mt-1 block">
                                (Template content is truncated for display)
                            </Text>
                        </div>
                    ) : (
                        <pre className="whitespace-pre-wrap">{content}</pre>
                    )}
                </div>
            </div>
        );
    };

    const expandedRowRender = (record: any) => {
        return (
            <div className="px-4 py-2 space-y-4">
                {record.templates.map((template: TextTemplateDTO) => (
                    <div key={`${template.name}_${template.language}`} className="mb-4">
                        {renderLanguageContent(template)}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div>
            <Table
                columns={columns}
                dataSource={groupedTemplates}
                rowKey="key"
                expandable={{
                    expandedRowRender,
                    onExpand: handleExpand,
                    expandedRowKeys,
                }}
                loading={isLoading}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} templates`
                }}
            />
        </div>
    );
};

export default TemplateList;